import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { blogService } from '../../services/blogService';
import BlogPostCard from './components/BlogPostCard';

const BlogListing = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');
  const [showAllCategories, setShowAllCategories] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterPosts();
  }, [searchQuery, selectedCategory, posts]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsData, categoriesData] = await Promise.all([
        blogService?.getPublishedPosts(),
        blogService?.getAllCategories()
      ]);
      setPosts(postsData);
      setFilteredPosts(postsData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = posts;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered?.filter(post => post?.categoryId === selectedCategory);
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        (post) =>
          post?.title?.toLowerCase()?.includes(query) ||
          post?.content?.toLowerCase()?.includes(query)
      );
    }

    setFilteredPosts(filtered);
  };

  const handlePostClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
  };

  const handleNewsletterSubmit = async (e) => {
    e?.preventDefault();
    setNewsletterError('');
    setNewsletterSuccess(false);

    if (!newsletterEmail?.trim()) {
      setNewsletterError('Lütfen e-posta adresinizi girin');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(newsletterEmail)) {
      setNewsletterError('Geçerli bir e-posta adresi girin');
      return;
    }

    try {
      setNewsletterLoading(true);
      await blogService?.subscribeToNewsletter(newsletterEmail);
      setNewsletterSuccess(true);
      setNewsletterEmail('');
      setTimeout(() => setNewsletterSuccess(false), 5000);
    } catch (err) {
      console.error('Newsletter subscription failed:', err);
      setNewsletterError(err?.message || 'Abonelik başarısız oldu');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const truncateContent = (content, maxLength = 200) => {
    const plainText = content
      ?.replace(/#{1,6}\s/g, '')
      ?.replace(/\*\*/g, '')
      ?.replace(/\*/g, '')
      ?.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      ?.replace(/\n/g, ' ');

    if (plainText?.length <= maxLength) return plainText;
    return plainText?.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content?.split(/\s+/)?.length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} dk okuma`;
  };

  const getCategoryColor = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      green: 'bg-green-100 text-green-700 border-green-200',
      red: 'bg-red-100 text-red-700 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200',
      pink: 'bg-pink-100 text-pink-700 border-pink-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      gray: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colorMap?.[color] || colorMap?.blue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Header />
        <div className="flex flex-col items-center justify-center py-32">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mb-4"></div>
          <p className="text-gray-600 font-medium">Blog yazıları yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary to-secondary text-primary-foreground py-8 md:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">
                SUP Dünyasından Haberler & Rehberler
              </h1>
              <p className="text-sm md:text-base lg:text-lg opacity-90">
                Stand Up Paddle dünyasından en güncel haberler, ipuçları, etkinlik duyuruları ve topluluk hikayeleri
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Enhanced Search Section */}
      <div className="container mx-auto px-4 -mt-10 relative z-20">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
            <div className="relative">
              <Icon
                name="Search"
                className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder="Blog yazılarında ara... (örn: SUP teknikleri, etkinlikler)"
                className="w-full pl-14 pr-5 py-4 text-lg border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Icon name="X" className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Enhanced Category Filters */}
      {categories?.length > 0 && (
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">Kategoriler</h3>
                <p className="text-gray-600 text-sm">İlgilendiğiniz konuları keşfedin</p>
              </div>
              {selectedCategory && (
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-primary hover:text-primary-dark font-medium flex items-center transition-colors"
                >
                  <Icon name="X" className="w-4 h-4 mr-1" />
                  Filtreyi Temizle
                </button>
              )}
            </div>
            
            {/* Horizontal Scrollable Category List */}
            <div className="relative">
              <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <button
                  onClick={() => handleCategoryClick(null)}
                  className={`flex-shrink-0 px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 ${
                    !selectedCategory
                      ? 'bg-primary text-white shadow-lg shadow-primary/30'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon name="Grid" className="w-4 h-4 mr-2" />
                    Tümü
                  </div>
                </button>
                {(showAllCategories ? categories : categories?.slice(0, 5))?.map((category) => (
                  <button
                    key={category?.id}
                    onClick={() => handleCategoryClick(category?.id)}
                    className={`flex-shrink-0 px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 ${
                      selectedCategory === category?.id
                        ? 'shadow-lg ' + getCategoryColor(category?.color)
                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {category?.name}
                  </button>
                ))}
                {categories?.length > 5 && (
                  <button
                    onClick={() => setShowAllCategories(!showAllCategories)}
                    className="flex-shrink-0 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 border-2 border-gray-300 hover:border-primary hover:shadow-md transition-all transform hover:scale-105"
                  >
                    <div className="flex items-center">
                      <Icon name={showAllCategories ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 mr-2" />
                      {showAllCategories ? 'Daha Az' : `+${categories?.length - 5} Daha Fazla`}
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Content Section */}
      <div className="container mx-auto px-4 pb-16">
        {error && (
          <div className="max-w-6xl mx-auto mb-8">
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-lg shadow-sm flex items-start">
              <Icon name="AlertCircle" className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Bir hata oluştu</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-6xl mx-auto">
          {/* Posts Section - Full Width */}
          <div>
            {filteredPosts?.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl shadow-md border border-gray-100">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="FileText" className="w-12 h-12 text-gray-300" />
                </div>
                <h3 className="text-2xl font-bold text-gray-700 mb-3">
                  {searchQuery || selectedCategory ? 'Sonuç bulunamadı' : 'Henüz blog yazısı yok'}
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {searchQuery || selectedCategory
                    ? 'Farklı anahtar kelimeler veya kategoriler deneyin' :'Yakında yeni içerikler eklenecek. Bültenimize abone olarak haberdar olun!'}
                </p>
                {(searchQuery || selectedCategory) && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory(null);
                    }}
                    className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                  >
                    <Icon name="RotateCcw" className="w-4 h-4 mr-2" />
                    Tüm Yazıları Göster
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Results Count */}
                {(searchQuery || selectedCategory) && (
                  <div className="mb-6 flex items-center justify-between bg-blue-50 border border-blue-200 px-6 py-4 rounded-xl">
                    <div className="flex items-center text-blue-800">
                      <Icon name="Filter" className="w-5 h-5 mr-2" />
                      <span className="font-semibold text-lg">{filteredPosts?.length}</span>
                      <span className="ml-2">sonuç bulundu</span>
                    </div>
                  </div>
                )}

                {/* Posts Grid */}
                <div className="space-y-8">
                  {filteredPosts?.map((post) => (
                    <BlogPostCard
                      key={post?.id}
                      post={post}
                      onClick={() => handlePostClick(post?.slug)}
                      getCategoryColor={getCategoryColor}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Enhanced Newsletter Section - Below Blog List */}
          <div className="mt-12">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-lg shadow-sm p-4 md:p-5">
              <div className="max-w-lg mx-auto text-center">
                <div className="inline-flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg mb-2">
                  <Icon name="Mail" className="w-4 h-4 text-primary" />
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1">
                  Bültene Abone Ol
                </h3>
                <p className="text-gray-600 text-xs md:text-sm mb-4 leading-relaxed">
                  Yeni blog yazıları ve etkinlik duyurularından haberdar olun.
                </p>

                <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e?.target?.value)}
                      placeholder="E-posta adresiniz"
                      disabled={newsletterLoading}
                      className="flex-1 px-3 py-2 text-xs bg-white text-gray-900 placeholder-gray-400 border border-gray-200 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
                    />
                    <button
                      type="submit"
                      disabled={newsletterLoading}
                      className="sm:w-auto bg-primary text-white px-4 py-2 rounded-md text-xs font-semibold hover:bg-primary-dark transition-all disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center shadow-sm hover:shadow-md"
                    >
                      {newsletterLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent mr-1.5"></div>
                          Kaydediliyor
                        </>
                      ) : (
                        <>
                          <Icon name="Send" className="w-3 h-3 mr-1.5" />
                          Abone Ol
                        </>
                      )}
                    </button>
                  </div>

                  {newsletterError && (
                    <div className="text-xs text-red-600 bg-red-50 px-2.5 py-1.5 rounded-md flex items-start">
                      <Icon name="AlertCircle" className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                      <span>{newsletterError}</span>
                    </div>
                  )}

                  {newsletterSuccess && (
                    <div className="text-xs text-green-600 bg-green-50 px-2.5 py-1.5 rounded-md flex items-center">
                      <Icon name="CheckCircle" className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span>Başarıyla abone oldunuz!</span>
                    </div>
                  )}
                </form>

                <div className="mt-3 flex items-center justify-center text-xs text-gray-500">
                  <Icon name="Lock" className="w-3 h-3 mr-1 flex-shrink-0" />
                  <p>E-posta adresiniz güvende. Spam göndermiyoruz.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogListing;